import { useState, useEffect, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import AdminLayout from "../components/AdminLayout";
import {
  Card, SectionCard, Button, Input, Select, Textarea, Modal, PageLoader,
  IconButton, StickyActions, Divider, cx,
} from "../components/UI";
import { useAuth } from "../context/AuthContext";
import api from "../utils/api";
import toast from "react-hot-toast";
import { SiteSearchPopup } from "../components/SiteSearchPopup";
import ClientSelectPopup from "../components/ClientSelectPopup";
import { Plus, Trash2, Receipt, Building2, Landmark } from "lucide-react";

const UNITS = ["cent", "acre", "day", "time", "km", "m"];
const INIT_ITEM = { description: "", quantity: 1, unit: "nos", rate: 0, amount: 0, hsnCode: "", date: "" };

const GST_PRESETS = [
  { label: "No GST", type: "none", rate: 0 },
  { label: "Kerala 18%", type: "cgst_sgst", rate: 18 },
  { label: "Other State 18%", type: "igst", rate: 18 },
];

const money = (n) => `₹${Number(n || 0).toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

/* ── read-only company field ── */
function ReadOnlyField({ label, value, mono = false }) {
  return (
    <div>
      <label className="text-[11px] font-semibold text-ink-400 uppercase tracking-wider block mb-1">{label}</label>
      <div className={cx("w-full bg-ink-50 border border-ink-200 text-ink-600 rounded-xl px-3 py-2 text-sm min-h-[38px] break-words", mono && "font-mono")}>
        {value || <span className="text-ink-300">—</span>}
      </div>
    </div>
  );
}

/* ── add line item ── */
function AddItemModal({ open, onAdd, onClose }) {
  const [item, setItem] = useState({ ...INIT_ITEM });
  const [error, setError] = useState("");

  const set = (k, v) => {
    setItem(prev => {
      const next = { ...prev, [k]: v };
      if (k === "quantity" || k === "rate") {
        next.amount = +((parseFloat(next.quantity) || 0) * (parseFloat(next.rate) || 0)).toFixed(2);
      }
      return next;
    });
  };

  const handleAdd = () => {
    if (!item.description.trim()) { setError("Description is required."); return; }
    onAdd(item);
    setItem({ ...INIT_ITEM });
    setError("");
    onClose();
  };

  return (
    <Modal open={open} onClose={onClose} title="Add Line Item" icon={<Plus size={18} />}>
      <div className="space-y-4">
        <Input label="Description" required autoFocus placeholder="e.g. Survey Services" error={error}
          value={item.description} onChange={e => { set("description", e.target.value); setError(""); }} />
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <Input label="HSN / SAC Code" placeholder="Optional" value={item.hsnCode} onChange={e => set("hsnCode", e.target.value)} className="font-mono" />
          <Input label="Date" type="date" value={item.date} onChange={e => set("date", e.target.value)} />
        </div>
        <div className="grid grid-cols-3 gap-3">
          <Input label="Quantity" type="number" min="0" step="0.01" value={item.quantity} onChange={e => set("quantity", e.target.value)} />
          <Select label="Unit" value={item.unit} onChange={e => set("unit", e.target.value)}>
            {UNITS.map(u => <option key={u} value={u}>{u}</option>)}
          </Select>
          <Input label="Rate" type="number" min="0" step="0.01" suffix="₹" value={item.rate} onChange={e => set("rate", e.target.value)} />
        </div>

        <div className="flex items-center justify-between bg-emerald-50 border border-emerald-200 rounded-2xl px-4 py-3">
          <span className="text-sm text-emerald-700 font-medium">Amount</span>
          <span className="text-lg font-bold text-emerald-700 font-mono tnum">{money(item.amount)}</span>
        </div>

        <StickyActions>
          <Button type="button" variant="secondary" onClick={onClose} block className="sm:w-auto">Cancel</Button>
          <Button type="button" onClick={handleAdd} block className="sm:w-auto">Add Item</Button>
        </StickyActions>
      </div>
    </Modal>
  );
}

export default function AdminBillEditor() {
  const { id } = useParams();
  const isEdit = !!id && id !== "new";
  const navigate = useNavigate();
  const { user } = useAuth();
  const [loading, setLoading] = useState(isEdit);
  const [saving, setSaving] = useState(false);
  const [nextNum, setNextNum] = useState("");
  const [showModal, setShowModal] = useState(false);

  const [form, setForm] = useState({
    clientId: "", siteId: "", billDate: new Date().toISOString().split("T")[0],
    subject: "", description: "",
    items: [{ ...INIT_ITEM }],
    discount: 0,
    gstPreset: "0", gstType: "none", gstRate: 0,
    notes: "",
    companyName: "", companyAddress: "", companyGST: "", companyEmail: "", companyPhone: "",
    companyBankDetails: { bankName: "", accountName: "", accountNumber: "", ifscCode: "", branch: "", upiId: "" },
  });

  const [calc, setCalc] = useState({ subTotal: 0, discount: 0, taxable: 0, cgst: 0, sgst: 0, igst: 0, grandTotal: 0 });

  const recalc = useCallback((f) => {
    const sub = f.items.reduce((s, i) => s + (parseFloat(i.amount) || 0), 0);
    const disc = parseFloat(f.discount) || 0;
    const taxable = Math.max(0, sub - disc);
    const rate = parseFloat(f.gstRate) || 0;
    let cgst = 0, sgst = 0, igst = 0;
    if (f.gstType === "cgst_sgst") { cgst = +(taxable * rate / 2 / 100).toFixed(2); sgst = cgst; }
    else if (f.gstType === "igst") { igst = +(taxable * rate / 100).toFixed(2); }
    setCalc({ subTotal: sub, discount: disc, taxable, cgst, sgst, igst, grandTotal: +(taxable + cgst + sgst + igst).toFixed(2) });
  }, []);

  useEffect(() => { recalc(form); }, [form, recalc]);

  // Reserve the next invoice number so the header can show what will be assigned
  useEffect(() => {
    if (isEdit) return;
    api.get("/bills/next-number")
      .then(({ data }) => setNextNum(data.data.billNumber))
      .catch(() => { });
  }, [isEdit]);

  useEffect(() => {
    if (!isEdit) return;
    api.get(`/bills/${id}`)
      .then(({ data }) => {
        const b = data.data;
        const preset = GST_PRESETS.findIndex(p => p.type === b.gstType && p.rate === b.gstRate);
        setForm({
          clientId: b.clientId?._id || "",
          siteId: b.siteId?._id || "",
          billDate: b.billDate || "",
          subject: b.subject || "",
          description: b.description || "",
          items: b.items?.length ? b.items : [{ ...INIT_ITEM }],
          discount: b.discount || 0,
          gstPreset: String(preset >= 0 ? preset : 0),
          gstType: b.gstType || "none",
          gstRate: b.gstRate || 0,
          notes: b.notes || "",
          companyName: b.companyName || "",
          companyAddress: b.companyAddress || "",
          companyGST: b.companyGST || "",
          companyEmail: b.companyEmail || "",
          companyPhone: b.companyPhone || "",
          companyBankDetails: b.companyBankDetails || { bankName: "", accountName: "", accountNumber: "", ifscCode: "", branch: "", upiId: "" },
        });
      })
      .catch(() => toast.error("Failed to load bill"))
      .finally(() => setLoading(false));
  }, [id, isEdit]);

  // New invoices inherit the admin's company/bank details
  useEffect(() => {
    if (isEdit || !user) return;
    setForm(f => ({
      ...f,
      companyName: user.company || "",
      companyEmail: user.email || "",
      companyPhone: user.phone || "",
      companyGST: user.gstNumber || "",
      companyAddress: user.address || "",
      companyBankDetails: user.bankDetails
        ? {
          bankName: user.bankDetails.bankName || "",
          accountName: user.bankDetails.accountName || "",
          accountNumber: user.bankDetails.accountNumber || "",
          ifscCode: user.bankDetails.ifscCode || "",
          branch: user.bankDetails.branch || "",
          upiId: user.bankDetails.upiId || "",
        }
        : f.companyBankDetails,
    }));
  }, [user, isEdit]);

  const setField = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const applyGSTPreset = (idx) => {
    const preset = GST_PRESETS[parseInt(idx)];
    setForm(f => ({ ...f, gstPreset: idx, gstType: preset.type, gstRate: preset.rate }));
  };

  const updateItem = (i, k, v) => {
    setForm(f => {
      const items = [...f.items];
      items[i] = { ...items[i], [k]: v };
      if (k === "quantity" || k === "rate") {
        items[i].amount = +((parseFloat(items[i].quantity) || 0) * (parseFloat(items[i].rate) || 0)).toFixed(2);
      }
      return { ...f, items };
    });
  };

  const addItemFromModal = (newItem) => setForm(f => ({ ...f, items: [...f.items, newItem] }));
  const removeItem = (i) => setForm(f => ({ ...f, items: f.items.filter((_, j) => j !== i) }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.clientId) return toast.error("Select a client");
    if (!form.items.length) return toast.error("Add at least one item");
    setSaving(true);
    try {
      const payload = { ...form };
      delete payload.gstPreset;
      if (isEdit) {
        await api.put(`/bills/${id}`, payload);
        toast.success("Bill updated!");
        navigate(`/admin/bills/${id}`);
      } else {
        const { data } = await api.post("/bills", payload);
        toast.success("Bill created!");
        navigate(`/admin/bills/${data.data._id}`);
      }
    } catch (err) {
      toast.error(err.response?.data?.message || "Failed");
    } finally { setSaving(false); }
  };

  if (loading) return <AdminLayout><PageLoader label="Loading invoice…" /></AdminLayout>;

  const hasGST = form.gstType !== "none";
  const bank = user?.bankDetails || {};
  const hasBankDetails = bank.bankName || bank.accountNumber || bank.ifscCode || bank.accountName || bank.branch;

  const cellInput = "w-full bg-white border border-ink-300 text-ink-800 rounded-lg px-2.5 py-2 text-sm focus:outline-none focus:border-brand-500 focus:ring-2 focus:ring-brand-100 transition-all";

  return (
    <AdminLayout>
      <AddItemModal open={showModal} onAdd={addItemFromModal} onClose={() => setShowModal(false)} />

      <form onSubmit={handleSubmit} className="space-y-5">

        {/* ══ Header ══ */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div className="min-w-0">
            <h1 className="text-xl sm:text-2xl font-bold text-ink-900 font-display">{isEdit ? "Edit Invoice" : "New Invoice"}</h1>
            {!isEdit && nextNum && (
              <p className="text-ink-400 text-sm mt-1">
                Will be assigned <span className="font-mono text-brand-600 font-semibold">{nextNum}</span>
              </p>
            )}
          </div>
          <div className="flex gap-2 shrink-0">
            <Button type="button" variant="secondary" onClick={() => navigate(-1)} className="flex-1 sm:flex-none">Cancel</Button>
            <Button type="submit" loading={saving} className="flex-1 sm:flex-none">{isEdit ? "Save Changes" : "Create Invoice"}</Button>
          </div>
        </div>

        <div className="grid grid-cols-1 xl:grid-cols-3 gap-4 sm:gap-5">

          {/* ══ Left column ══ */}
          <div className="xl:col-span-2 space-y-4 sm:space-y-5">

            <SectionCard title="Client & Project" icon={<Building2 size={16} />} bodyClass="p-4 sm:p-5 space-y-4">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <ClientSelectPopup label="Client" value={form.clientId} onChange={(cid) => setForm({ ...form, clientId: cid })} />
                <SiteSearchPopup value={form.siteId} onChange={v => setField("siteId", v)} />
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <Input label="Bill Date" type="date" required value={form.billDate} onChange={e => setField("billDate", e.target.value)} />
                <Input label="Subject" placeholder="e.g. Survey Services" value={form.subject} onChange={e => setField("subject", e.target.value)} />
              </div>
            </SectionCard>

            <SectionCard
              title="Line Items"
              subtitle={`${form.items.length} item${form.items.length !== 1 ? "s" : ""}`}
              icon={<Receipt size={16} />}
              action={<Button type="button" size="xs" variant="outline" icon={<Plus size={13} />} onClick={() => setShowModal(true)}>Add</Button>}
              bodyClass="p-4 sm:p-5"
            >
              {/* desktop grid */}
              <div className="hidden lg:block">
                {/* fixed layout: the description absorbs the slack, so the editor never scrolls sideways */}
                <table className="w-full table-fixed text-sm">
                  <thead>
                    <tr className="border-b border-ink-200">
                      <th className="text-left pb-2 text-[11px] text-ink-400 font-semibold uppercase w-8">#</th>
                      <th className="text-left pb-2 text-[11px] text-ink-400 font-semibold uppercase">Description</th>
                      <th className="text-left pb-2 text-[11px] text-ink-400 font-semibold uppercase w-32">Date</th>
                      {hasGST && <th className="text-center pb-2 text-[11px] text-ink-400 font-semibold uppercase w-24">SAC</th>}
                      <th className="text-left pb-2 text-[11px] text-ink-400 font-semibold uppercase w-20">Qty</th>
                      <th className="text-left pb-2 text-[11px] text-ink-400 font-semibold uppercase w-24">Unit</th>
                      <th className="text-right pb-2 text-[11px] text-ink-400 font-semibold uppercase w-28">Rate</th>
                      <th className="text-right pb-2 text-[11px] text-ink-400 font-semibold uppercase w-28">Amount</th>
                      <th className="w-10" />
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-ink-100">
                    {form.items.map((item, i) => (
                      <tr key={i}>
                        <td className="py-2.5 text-ink-400 text-xs tnum">{i + 1}</td>
                        <td className="py-2.5 pr-2">
                          <input type="text" placeholder="Description of service…" value={item.description}
                            onChange={e => updateItem(i, "description", e.target.value)} className={cellInput} />
                          <input type="text" placeholder="HSN / SAC (optional)" value={item.hsnCode || ""}
                            onChange={e => updateItem(i, "hsnCode", e.target.value)}
                            className="w-full bg-transparent text-ink-400 text-xs px-2.5 mt-1 focus:outline-none placeholder-ink-300" />
                        </td>
                        <td className="py-2.5 pr-2">
                          <input type="date" value={item.date || ""} onChange={e => updateItem(i, "date", e.target.value)} className={cx(cellInput, "text-xs")} />
                        </td>
                        {hasGST && (
                          <td className="py-2.5 pr-2">
                            <div className="bg-ink-50 border border-ink-200 text-ink-500 rounded-lg px-2 py-2 text-sm font-mono text-center">
                              {user?.sacCode || "—"}
                            </div>
                          </td>
                        )}
                        <td className="py-2.5 pr-2">
                          <input type="number" min="0" step="0.01" value={item.quantity}
                            onChange={e => updateItem(i, "quantity", e.target.value)} className={cx(cellInput, "text-center tnum")} />
                        </td>
                        <td className="py-2.5 pr-2">
                          <select value={item.unit} onChange={e => updateItem(i, "unit", e.target.value)} className={cellInput}>
                            {UNITS.map(u => <option key={u} value={u}>{u}</option>)}
                          </select>
                        </td>
                        <td className="py-2.5 pr-2">
                          <input type="number" min="0" step="0.01" value={item.rate}
                            onChange={e => updateItem(i, "rate", e.target.value)} className={cx(cellInput, "text-right tnum")} />
                        </td>
                        <td className="py-2.5 text-right text-ink-800 font-mono text-sm font-semibold tnum">{money(item.amount)}</td>
                        <td className="py-2.5 pl-2">
                          {form.items.length > 1 && (
                            <IconButton size="sm" variant="danger" icon={<Trash2 size={14} />} onClick={() => removeItem(i)} title="Remove item" />
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* mobile stacked editor */}
              <div className="lg:hidden space-y-3">
                {form.items.map((item, i) => (
                  <div key={i} className="border border-ink-200 rounded-2xl p-3 space-y-3 bg-ink-50/40">
                    <div className="flex items-center justify-between">
                      <span className="text-[11px] font-semibold text-ink-400 uppercase tracking-wider">Item {i + 1}</span>
                      {form.items.length > 1 && (
                        <IconButton size="xs" variant="danger" icon={<Trash2 size={14} />} onClick={() => removeItem(i)} title="Remove item" />
                      )}
                    </div>
                    <input type="text" placeholder="Description of service…" value={item.description}
                      onChange={e => updateItem(i, "description", e.target.value)} className={cellInput} />
                    <div className="grid grid-cols-2 gap-2.5">
                      <input type="text" placeholder="HSN / SAC" value={item.hsnCode || ""}
                        onChange={e => updateItem(i, "hsnCode", e.target.value)} className={cx(cellInput, "font-mono text-xs")} />
                      <input type="date" value={item.date || ""} onChange={e => updateItem(i, "date", e.target.value)} className={cx(cellInput, "text-xs")} />
                    </div>
                    <div className="grid grid-cols-3 gap-2.5">
                      <input type="number" min="0" step="0.01" value={item.quantity} placeholder="Qty"
                        onChange={e => updateItem(i, "quantity", e.target.value)} className={cx(cellInput, "text-center tnum")} />
                      <select value={item.unit} onChange={e => updateItem(i, "unit", e.target.value)} className={cellInput}>
                        {UNITS.map(u => <option key={u} value={u}>{u}</option>)}
                      </select>
                      <input type="number" min="0" step="0.01" value={item.rate} placeholder="Rate"
                        onChange={e => updateItem(i, "rate", e.target.value)} className={cx(cellInput, "text-right tnum")} />
                    </div>
                    <div className="flex justify-between items-center pt-2 border-t border-ink-200">
                      <span className="text-xs text-ink-500">Amount</span>
                      <span className="font-mono font-semibold text-ink-900 tnum">{money(item.amount)}</span>
                    </div>
                  </div>
                ))}
              </div>

              <button type="button" onClick={() => setShowModal(true)}
                className="mt-3 w-full border border-dashed border-ink-300 hover:border-brand-500 text-ink-400 hover:text-brand-600 hover:bg-brand-50/50 rounded-xl py-2.5 text-sm font-medium transition-all flex items-center justify-center gap-1.5">
                <Plus size={15} /> Add item
              </button>
            </SectionCard>

            <SectionCard title="Notes" bodyClass="p-4 sm:p-5">
              <Textarea rows={3} value={form.notes} onChange={e => setField("notes", e.target.value)}
                placeholder="Additional notes for the client…" />
            </SectionCard>
          </div>

          {/* ══ Right column ══ */}
          <div className="space-y-4 sm:space-y-5">

            <SectionCard title="GST & Discount" bodyClass="p-4 sm:p-5 space-y-3">
              <Select label="GST Type" value={form.gstPreset} onChange={e => applyGSTPreset(e.target.value)}>
                {GST_PRESETS.map((p, i) => <option key={i} value={String(i)}>{p.label}</option>)}
              </Select>

              {hasGST && (
                <div className="p-3 bg-indigo-50 border border-indigo-200 rounded-xl text-xs text-indigo-700 space-y-1">
                  {form.gstType === "cgst_sgst" ? (
                    <>
                      <div className="flex justify-between"><span>CGST @ {form.gstRate / 2}%</span><span className="font-mono tnum">{money(calc.cgst)}</span></div>
                      <div className="flex justify-between"><span>SGST @ {form.gstRate / 2}%</span><span className="font-mono tnum">{money(calc.sgst)}</span></div>
                    </>
                  ) : (
                    <div className="flex justify-between"><span>IGST @ {form.gstRate}%</span><span className="font-mono tnum">{money(calc.igst)}</span></div>
                  )}
                </div>
              )}

              <Input label="Discount" type="number" min="0" step="0.01" suffix="₹"
                value={form.discount} onChange={e => setField("discount", e.target.value)} />
            </SectionCard>

            {/* sticky so the running total stays visible while editing rows */}
            <SectionCard title="Summary" className="xl:sticky xl:top-5" bodyClass="p-4 sm:p-5">
              <div className="space-y-2 text-sm">
                <div className="flex justify-between text-ink-500"><span>Sub Total</span><span className="font-mono tnum">{money(calc.subTotal)}</span></div>
                {calc.discount > 0 && (
                  <>
                    <div className="flex justify-between text-red-600"><span>Discount</span><span className="font-mono tnum">− {money(calc.discount)}</span></div>
                    <div className="flex justify-between text-ink-500 border-t border-ink-200 pt-2"><span>Taxable</span><span className="font-mono tnum">{money(calc.taxable)}</span></div>
                  </>
                )}
                {calc.cgst > 0 && <div className="flex justify-between text-indigo-600"><span>CGST</span><span className="font-mono tnum">{money(calc.cgst)}</span></div>}
                {calc.sgst > 0 && <div className="flex justify-between text-indigo-600"><span>SGST</span><span className="font-mono tnum">{money(calc.sgst)}</span></div>}
                {calc.igst > 0 && <div className="flex justify-between text-indigo-600"><span>IGST</span><span className="font-mono tnum">{money(calc.igst)}</span></div>}
                <div className="flex justify-between items-center border-t-2 border-ink-200 pt-3 mt-1">
                  <span className="font-bold text-ink-900">Grand Total</span>
                  <span className="text-lg font-bold font-display text-emerald-600 tnum">{money(calc.grandTotal)}</span>
                </div>
              </div>
            </SectionCard>

            <SectionCard
              title="Your Company"
              icon={<Building2 size={16} />}
              action={<span className="text-[10px] text-ink-500 bg-ink-100 rounded-full px-2 py-1 font-medium">Read only</span>}
              bodyClass="p-4 sm:p-5"
            >
              <div className="space-y-3">
                <ReadOnlyField label="Company Name" value={user?.company} />
                <ReadOnlyField label="GSTIN" value={user?.gstNumber} mono />
                <ReadOnlyField label="SAC Code" value={user?.sacCode} mono />
                <ReadOnlyField label="Address" value={user?.address} />
                <ReadOnlyField label="Email" value={user?.email} />
                <ReadOnlyField label="Phone" value={user?.phone} />
              </div>

              {hasBankDetails && (
                <>
                  <Divider label="Bank details" className="my-4" />
                  <div className="space-y-3">
                    {bank.accountName && <ReadOnlyField label="Account Name" value={bank.accountName} />}
                    {bank.bankName && <ReadOnlyField label="Bank Name" value={bank.bankName} />}
                    {bank.branch && <ReadOnlyField label="Branch" value={bank.branch} />}
                    {bank.accountNumber && <ReadOnlyField label="Account Number" value={bank.accountNumber} mono />}
                    {bank.ifscCode && <ReadOnlyField label="IFSC Code" value={bank.ifscCode} mono />}
                    {bank.upiId && <ReadOnlyField label="UPI ID" value={bank.upiId} mono />}
                  </div>
                </>
              )}
            </SectionCard>
          </div>
        </div>

        {/* mobile save bar — the header buttons scroll away on long forms */}
        <Card className="xl:hidden p-3 sticky bottom-3 flex items-center justify-between gap-3 shadow-raised">
          <div className="min-w-0">
            <div className="text-[11px] text-ink-400 uppercase tracking-wider font-semibold">Grand total</div>
            <div className="text-lg font-bold font-display text-emerald-600 tnum">{money(calc.grandTotal)}</div>
          </div>
          <Button type="submit" loading={saving} className="shrink-0">{isEdit ? "Save" : "Create Invoice"}</Button>
        </Card>
      </form>
    </AdminLayout>
  );
}
